import React, { forwardRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { InputProps } from '@business/interfaces/input';
import { SVGAssets } from '@business/assets/vectors';

const getPasswordDecorator = (
  inputType: string,
  callback: (type: string) => void
) => {
  const Icon =
    inputType === 'text' ? SVGAssets.EyeIcon : SVGAssets.EyeClosedIcon;
  const dataTestId =
    inputType === 'text' ? 'visibility-on-icon' : 'visibility-off-icon';
  const nextType = inputType === 'text' ? 'password' : 'text';

  return (
    <Icon
      className="passwordToggle"
      data-testid={dataTestId}
      onClick={() => callback(nextType)}
    />
  );
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      inputWrapperClassName,
      label,
      error,
      startDecorator,
      endDecorator,
      control,
      type = 'text',
      name,
      required = false,
      showPasswordToggle = false,
      isWhiteTheme = false,
      ...props
    },
    ref
  ) => {
    const [inputType, setInputType] = useState<string>(type);

    const handleEndDecoratorVisibility = (type: string) => setInputType(type);

    const renderInputField = (
      fieldProps?: any,
      fieldError?: { message?: string }
    ) => {
      const actualError = fieldError?.message || error;
      const isDisabled = props?.disabled || props?.readOnly;

      return (
        <div className="inputContainer">
          {label && (
            <label
              htmlFor={name}
              data-testid="input-label"
              className="inputLabel ">
              {label}
              {required && <span className="requiredAsterisk">*</span>}
            </label>
          )}
          <div
            className={`${
              type === 'password'
                ? 'inputWrapperPassword'
                : isWhiteTheme
                  ? 'inputWrapper-white'
                  : 'inputWrapper'
            } ${actualError ? 'error' : ''} ${isDisabled ? '!bg-gray-100 cursor-not-allowed' : ''} ${inputWrapperClassName || ''}`}>
            {startDecorator}
            <input
              {...props}
              {...fieldProps}
              ref={fieldProps?.ref || ref}
              autoComplete="off"
              id={name}
              name={name}
              type={inputType}
              data-testid={`input-field-${name}`}
              className={`inputField ${isDisabled ? 'cursor-not-allowed text-gray-500' : ''}`}
            />
            {type === 'password' &&
            (showPasswordToggle || typeof endDecorator === 'undefined')
              ? getPasswordDecorator(inputType, handleEndDecoratorVisibility)
              : endDecorator}
          </div>
          {actualError && <div className="errorMessage">{actualError}</div>}
        </div>
      );
    };

    if (control) {
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState }) =>
            renderInputField(field, fieldState.error)
          }
        />
      );
    }

    return renderInputField();
  }
);

Input.displayName = 'Input';

(Input as any).defaultProps = {
  type: 'text',
  placeholder: ' ',
  showPasswordToggle: false,
  inputWrapperClassName: ''
};

export default Input;
